import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/d756lbucsbv12mkudfh0";

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

export default function Contact() {
  const [form, setForm] = useState<FormData>({
    fullName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [charCount, setCharCount] = useState(0);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validate = (): boolean => {
    const newErrors: Partial<FormData> = {};
    if (!form.fullName.trim()) newErrors.fullName = "Full name is required.";
    if (!form.email.trim()) newErrors.email = "Email address is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Please enter a valid email.";
    if (!form.subject.trim()) newErrors.subject = "Subject is required.";
    if (!form.message.trim()) newErrors.message = "Message is required.";
    else if (form.message.length > 500)
      newErrors.message = "Message cannot exceed 500 characters.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "message") setCharCount(value.length);
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      const body = new URLSearchParams({
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
      });
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus("success");
        setForm({ fullName: "", email: "", phone: "", subject: "", message: "" });
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  const SUBJECTS = [
    "Product Enquiry – Dooars Tea",
    "Product Enquiry – Assam Tea",
    "Product Enquiry – Lakadong Turmeric",
    "Product Enquiry – White Turmeric",
    "Product Enquiry – Indian Spices",
    "Bulk Order / Quote Request",
    "Packaging & Labeling Query",
    "General Business Enquiry",
  ];

  return (
    <section id="contact" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block text-[#C9A227] text-xs font-bold tracking-widest uppercase border border-[#C9A227] px-3 py-1 rounded-full mb-4">
            Get In Touch
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B4332] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Contact Pandit Export
          </h2>
          <p className="text-gray-500 text-base md:text-lg max-w-xl mx-auto">
            Ready to source premium Indian products? Reach out to us and we&apos;ll get back to you quickly.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
          {/* Left – Contact Info */}
          <div className="w-full lg:w-[40%] flex-shrink-0">
            <div className="bg-[#1B4332] rounded-2xl p-8 h-full text-white">
              <h3 className="text-xl font-bold text-[#C9A227] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Pandit Export
              </h3>
              <p className="text-white/60 text-sm mb-8">Your trusted partner for premium Indian agricultural exports.</p>

              <div className="space-y-6">
                {/* Address */}
                <div className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C9A227] text-white flex-shrink-0">
                    <span className="text-lg"><i className="ri-map-pin-line"></i></span>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Address</p>
                    <p className="text-sm text-white/90 leading-relaxed">
                      Barobisha, Jorai Road,<br />
                      Alipurduar – 736207,<br />
                      West Bengal, India
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C9A227] text-white flex-shrink-0">
                    <span className="text-lg"><i className="ri-phone-line"></i></span>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Phone</p>
                    <a href="tel:+917679787266" className="text-sm text-white/90 hover:text-[#C9A227] transition-colors cursor-pointer">
                      +91 7679787266
                    </a>
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#25D366] text-white flex-shrink-0">
                    <span className="text-lg"><i className="ri-whatsapp-line"></i></span>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 uppercase tracking-wider mb-1">WhatsApp</p>
                    <a
                      href="https://wa.me/917584050023"
                      target="_blank"
                      rel="nofollow noreferrer"
                      className="text-sm text-white/90 hover:text-[#C9A227] transition-colors cursor-pointer"
                    >
                      +91 7584050023
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C9A227] text-white flex-shrink-0">
                    <span className="text-lg"><i className="ri-mail-line"></i></span>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 uppercase tracking-wider mb-1">Email</p>
                    <a href="mailto:info@panditexport.com" className="text-sm text-white/90 hover:text-[#C9A227] transition-colors cursor-pointer">
                      info@panditexport.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="my-8 border-t border-white/15"></div>

              {/* Map link */}
              <a
                href="https://maps.google.com/?q=Alipurduar,West+Bengal,India"
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center gap-2 text-sm text-[#C9A227] hover:text-white transition-colors cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line"></i>
                </span>
                View on Google Maps
              </a>
            </div>
          </div>

          {/* Right – Form */}
          <div className="w-full lg:w-[60%]">
            <div className="bg-white rounded-2xl border border-gray-100 p-8 md:p-10 shadow-sm">
              <h3 className="text-xl font-bold text-[#1B4332] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Send Us a Message
              </h3>

              {status === "success" ? (
                <div className="text-center py-14">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#D8F3DC] text-[#2D6A4F] rounded-full mx-auto mb-5 text-3xl">
                    <i className="ri-check-double-line"></i>
                  </div>
                  <h4 className="text-xl font-bold text-[#1B4332] mb-2">Message Sent!</h4>
                  <p className="text-gray-500 text-base">
                    Thank you! Your message has been sent successfully.
                  </p>
                  <p className="text-gray-400 text-sm mt-1">We&apos;ll get back to you within 24 hours.</p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-6 border border-[#2D6A4F] text-[#2D6A4F] font-semibold px-6 py-2.5 rounded-full hover:bg-[#2D6A4F] hover:text-white transition-all cursor-pointer whitespace-nowrap"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  {/* Full Name & Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Full Name <span className="text-[#C9A227]">*</span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={form.fullName}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className={`w-full border rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-300 outline-none transition-colors focus:border-[#2D6A4F] ${errors.fullName ? "border-red-400" : "border-gray-200"}`}
                      />
                      {errors.fullName && <p className="text-xs text-red-400 mt-1">{errors.fullName}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Email Address <span className="text-[#C9A227]">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className={`w-full border rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-300 outline-none transition-colors focus:border-[#2D6A4F] ${errors.email ? "border-red-400" : "border-gray-200"}`}
                      />
                      {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  {/* Phone & Subject */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+1 234 567 8900"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-300 outline-none focus:border-[#2D6A4F] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                        Subject <span className="text-[#C9A227]">*</span>
                      </label>
                      <select
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        className={`w-full border rounded-lg px-4 py-3 text-sm text-gray-800 outline-none focus:border-[#2D6A4F] transition-colors cursor-pointer ${errors.subject ? "border-red-400" : "border-gray-200"} ${!form.subject ? "text-gray-300" : ""}`}
                      >
                        <option value="" disabled>Select a subject</option>
                        {SUBJECTS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {errors.subject && <p className="text-xs text-red-400 mt-1">{errors.subject}</p>}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                      Message <span className="text-[#C9A227]">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      maxLength={500}
                      placeholder="Tell us what you need, quantity, destination country, etc."
                      className={`w-full border rounded-lg px-4 py-3 text-sm text-gray-800 placeholder-gray-300 outline-none resize-none focus:border-[#2D6A4F] transition-colors ${errors.message ? "border-red-400" : "border-gray-200"}`}
                    />
                    <div className="flex justify-between mt-1">
                      {errors.message ? (
                        <p className="text-xs text-red-400">{errors.message}</p>
                      ) : <span />}
                      <p className={`text-xs ${charCount > 450 ? "text-[#C9A227]" : "text-gray-400"} ml-auto`}>
                        {charCount}/500
                      </p>
                    </div>
                  </div>

                  {/* Error */}
                  {status === "error" && (
                    <p className="text-sm text-red-500 text-center">
                      Something went wrong. Please try again or contact us via WhatsApp.
                    </p>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full flex items-center justify-center gap-2 bg-[#2D6A4F] hover:bg-[#1B4332] disabled:bg-gray-300 text-white font-bold py-4 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {status === "sending" ? (
                      <>
                        <span className="w-5 h-5 flex items-center justify-center animate-spin">
                          <i className="ri-loader-4-line"></i>
                        </span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-send-plane-line"></i>
                        </span>
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
